import apiClient from '../utils/apiClient';

// Types
export interface BankAccount {
  _id: string;
  sellerId: {
    _id: string;
    /** نام فروشگاه (sellerId به Store اشاره می‌کند) */
    name?: string;
    businessName?: string;
    email: string;
    phone?: string;
    phoneNumber?: string;
  };
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  iban: string;
  cardNumber?: string;
  branchCode?: string;
  branchName?: string;
  accountType: 'checking' | 'savings' | 'business';
  isDefault: boolean;
  isVerified: boolean;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  verificationDocuments: string[];
  rejectionReason?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  verifiedAt?: string;
  lastUsedAt?: string;
  verifiedBy?: string;
  adminNotes?: string;
}

export interface BankAccountListResponse {
  bankAccounts: BankAccount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface BankAccountFilters {
  search?: string;
  verificationStatus?: 'pending' | 'verified' | 'rejected' | 'all';
  bankName?: string;
  accountType?: 'checking' | 'savings' | 'business' | 'all';
  dateFrom?: string;
  dateTo?: string;
  sellerId?: string;
}

export interface VerifyBankAccountData {
  verificationStatus: 'verified' | 'rejected';
  rejectionReason?: string;
  adminNotes?: string;
}

export interface BankAccountStats {
  total: number;
  pending: number;
  verified: number;
  rejected: number;
  recentVerifications: number;
  pendingDocuments: number;
}

// Constants
export const ACCOUNT_TYPES = {
  checking: 'جاری',
  savings: 'پس‌انداز',
  business: 'تجاری'
} as const;

export const VERIFICATION_STATUS = {
  pending: 'در انتظار تایید',
  verified: 'تایید شده',
  rejected: 'رد شده'
} as const;

export const IRANIAN_BANKS = [
  'بانک ملی ایران',
  'بانک ملت',
  'بانک صادرات ایران',
  'بانک تجارت',
  'بانک سپه',
  'بانک رفاه کارگران',
  'بانک کشاورزی',
  'بانک مسکن',
  'بانک صنعت و معدن',
  'بانک توسعه تعاون',
  'بانک توسعه صادرات ایران',
  'پست بانک ایران',
  'بانک اقتصاد نوین',
  'بانک پارسیان',
  'بانک پاسارگاد',
  'بانک سامان',
  'بانک سرمایه',
  'بانک سینا',
  'بانک شهر',
  'بانک دی',
  'بانک ایران زمین',
  'بانک گردشگری',
  'بانک آینده',
  'بانک خاورمیانه',
  'بانک کارآفرین',
  'بانک ملل',
  'بانک نور',
  'بانک رسالت',
  'بانک مهر ایران',
  'بانک قوامین',
  'بانک انصار',
  'بانک حکمت ایرانیان',
  'بانک مهر اقتصاد',
  'بانک کوثر',
  'بانک تات',
  'بانک ایران و ونزوئلا',
  'بانک مرکزی جمهوری اسلامی ایران'
];

class BankAccountService {
  /**
   * دریافت لیست تمام حساب‌های بانکی (ادمین)
   */
  async getAllBankAccounts(
    page: number = 1,
    limit: number = 10,
    filters: BankAccountFilters = {}
  ): Promise<{ data: BankAccountListResponse; success: boolean; message: string }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value && value !== 'all')
        )
      });

      const response = await apiClient.get(`/admin/bank-accounts?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دریافت حساب‌های بانکی');
    }
  }

  /**
   * دریافت جزئیات یک حساب بانکی (ادمین)
   */
  async getBankAccountById(accountId: string): Promise<BankAccount> {
    try {
      const response = await apiClient.get(`/admin/bank-accounts/${accountId}`);
      // بک‌اند: { success, message, data: <account> } (بدون تودرتوی account)
      return response.data?.data?.account || response.data?.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دریافت جزئیات حساب بانکی');
    }
  }

  /**
   * تایید یا رد حساب بانکی
   */
  async verifyBankAccount(accountId: string, data: VerifyBankAccountData): Promise<BankAccount> {
    try {
      const response = await apiClient.put(`/admin/bank-accounts/${accountId}/verify`, data);
      return response.data?.data?.account || response.data?.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در تایید/رد حساب بانکی');
    }
  }

  /**
   * دریافت آمار حساب‌های بانکی (ادمین)
   */
  async getBankAccountStats(): Promise<{ data: BankAccountStats; success: boolean; message: string }> {
    try {
      const response = await apiClient.get('/admin/bank-accounts/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دریافت آمار حساب‌های بانکی');
    }
  }

  /**
   * دریافت حساب‌های بانکی یک فروشنده خاص
   */
  async getSellerBankAccounts(sellerId: string): Promise<BankAccount[]> {
    try {
      const response = await apiClient.get(`/admin/bank-accounts/seller/${sellerId}`);
      return response.data?.data?.accounts || response.data?.data?.bankAccounts || response.data?.data || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دریافت حساب‌های بانکی فروشنده');
    }
  }

  /**
   * حذف حساب بانکی (ادمین)
   */
  async deleteBankAccount(accountId: string): Promise<void> {
    try {
      await apiClient.delete(`/admin/bank-accounts/${accountId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در حذف حساب بانکی');
    }
  }

  /**
   * دانلود مدارک تایید
   */
  async downloadVerificationDocument(accountId: string, documentPath: string): Promise<Blob> {
    try {
      const response = await apiClient.get(
        `/admin/bank-accounts/${accountId}/documents/${encodeURIComponent(documentPath)}`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'خطا در دانلود مدرک');
    }
  }

  /**
   * اعتبارسنجی شماره شبا
   */
  validateIban(iban: string): boolean {
    // حذف فاصله‌ها و تبدیل به حروف بزرگ
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    
    // بررسی فرمت کلی (IR + 24 رقم = 26 کاراکتر)
    if (!/^IR\d{24}$/.test(cleanIban)) {
      return false;
    }

    // بررسی checksum با الگوریتم mod-97
    // جابجایی: حروف اول به انتها
    const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
    
    // تبدیل حروف به اعداد (I=18, R=27)
    const numericString = rearranged.replace(/[A-Z]/g, (char) => {
      return (char.charCodeAt(0) - 65 + 10).toString();
    });
    
    // محاسبه mod 97 برای اعداد بزرگ
    const mod97 = (num: string): number => {
      let remainder = 0;
      for (let i = 0; i < num.length; i++) {
        remainder = (remainder * 10 + parseInt(num[i])) % 97;
      }
      return remainder;
    };
    
    return mod97(numericString) === 1;
  }

  /**
   * فرمت کردن شماره شبا
   */
  formatIban(iban: string): string {
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    return cleanIban.replace(/(.{4})/g, '$1 ').trim();
  }

  /**
   * فرمت کردن شماره حساب (نمایش امن)
   */
  formatAccountNumber(accountNumber: string, secure: boolean = true): string {
    if (!secure) return accountNumber;
    // نمایش فقط 4 رقم آخر
    if (accountNumber.length <= 4) return accountNumber;
    return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4);
  }

  /**
   * فرمت کردن شماره کارت (نمایش امن)
   */
  formatCardNumber(cardNumber: string, secure: boolean = true): string {
    if (!cardNumber) return '';
    if (!secure) return cardNumber;
    // نمایش فقط 4 رقم آخر
    if (cardNumber.length <= 4) return cardNumber;
    return '*'.repeat(cardNumber.length - 4) + cardNumber.slice(-4);
  }

  /**
   * فرمت کردن تاریخ
   */
  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(dateObj);
  }

  /**
   * فرمت کردن تاریخ و زمان
   */
  formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  }

  /**
   * دریافت رنگ وضعیت تایید
   */
  getVerificationStatusColor(status: string): 'warning' | 'success' | 'error' {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'verified':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  }

  /**
   * دریافت آیکون وضعیت تایید
   */
  getVerificationStatusIcon(status: string): string {
    switch (status) {
      case 'pending':
        return 'pending';
      case 'verified':
        return 'verified';
      case 'rejected':
        return 'cancel';
      default:
        return 'help';
    }
  }
}

export default new BankAccountService(); 